import { PACKAGE_TYPE } from "constants/packageOptions.constants";
import { PALLET_PRESETS, PALLET_TYPE } from "constants/palletOptions.constants";
import {
  BarrelsSettings,
  BigBagsSettings,
  BoxSettings,
  PackageSettings,
  PipesSettings,
  RollsSettings,
  SacksSettings,
} from "models/packageSettings";
import { CargoRow, Group, InputNumber, PalletRow } from "models/packingList";
import { getRandomPaletteColor } from "utils/color.utils";
import { ValueOf } from "utils/typeCheck.utils";

export const REQUIRED_CARGO_FIELDS = ["length", "width", "height", "weight", "qty"] as const;
export const REQUIRED_PALLET_FIELDS = ["length", "width", "height", "depth", "maxWeight"] as const;

export type RequiredCargoField = (typeof REQUIRED_CARGO_FIELDS)[number];
export type RequiredPalletField = (typeof REQUIRED_PALLET_FIELDS)[number];

export const isFilledRequiredNumber = (value: InputNumber): boolean => value != null && Number(value) > 0;

export const getInvalidRequiredCargoFields = (row: Pick<CargoRow, RequiredCargoField>): RequiredCargoField[] =>
  REQUIRED_CARGO_FIELDS.filter((field) => !isFilledRequiredNumber(row[field]));

export const hasInvalidRequiredCargoFields = (groups: Pick<Group, "cargoRows">[]): boolean =>
  groups.some((group) => group.cargoRows.some((row) => getInvalidRequiredCargoFields(row).length > 0));

export const isCustomPalletType = (palletType: string): boolean => !(palletType in PALLET_PRESETS);

export const getInvalidRequiredPalletFields = (
  row: Pick<PalletRow, RequiredPalletField | "palletType">
): RequiredPalletField[] => {
  if (!isCustomPalletType(row.palletType)) return [];
  return REQUIRED_PALLET_FIELDS.filter((field) => !isFilledRequiredNumber(row[field]));
};

export const hasInvalidRequiredPalletFields = (groups: Pick<Group, "usePallets" | "palletRow">[]): boolean =>
  groups.some((group) => group.usePallets && getInvalidRequiredPalletFields(group.palletRow).length > 0);

export const createDefaultPalletRow = (): PalletRow => ({
  id: crypto.randomUUID(),
  palletType: PALLET_TYPE.EU_PALLETS,
  ...PALLET_PRESETS[PALLET_TYPE.EU_PALLETS],
  color: getRandomPaletteColor(),
});

export const getNextGroupNumber = (groups: Pick<Group, "name">[]): number => {
  const usedNumbers = new Set<number>();

  for (const group of groups) {
    const match = /^Group (\d+)$/.exec(group.name.trim());
    if (match) usedNumbers.add(Number(match[1]));
  }

  let next = 1;
  while (usedNumbers.has(next)) next += 1;
  return next;
};

export const createDefaultGroup = (number = 1): Group => ({
  id: crypto.randomUUID(),
  name: `Group ${number}`,
  usePallets: false,
  cargoRows: [],
  palletRow: createDefaultPalletRow(),
});

const defaultBoxSettings: BoxSettings = {
  type: PACKAGE_TYPE.BOX,
  tiltToLength: false,
  tiltToWidth: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  fillRatio: { enabled: false, value: 0 },
};

const defaultBigBagsSettings: BigBagsSettings = {
  type: PACKAGE_TYPE.BIG_BAGS,
  tiltToLength: false,
  tiltToWidth: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  disableStacking: false,
};

const defaultBarrelsSettings: BarrelsSettings = {
  type: PACKAGE_TYPE.BARRELS,
  tilt: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  disableStacking: false,
};

const defaultSacksSettings: SacksSettings = {
  type: PACKAGE_TYPE.SACKS,
  tiltToLength: false,
  tiltToWidth: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  disableStacking: false,
};

const defaultRollsSettings: RollsSettings = {
  type: PACKAGE_TYPE.ROLLS,
  tilt: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  disableStacking: false,
};

const defaultPipesSettings: PipesSettings = {
  type: PACKAGE_TYPE.PIPES,
  tilt: false,
  layersCount: { enabled: false, value: 0 },
  mass: { enabled: false, value: 0 },
  heightStuffing: { enabled: false, value: 0 },
  disableStacking: false,
};

export const createDefaultPackageSettings = (packageValue: ValueOf<typeof PACKAGE_TYPE>): PackageSettings => {
  switch (packageValue) {
    case PACKAGE_TYPE.BIG_BAGS:
      return defaultBigBagsSettings;
    case PACKAGE_TYPE.BARRELS:
      return defaultBarrelsSettings;
    case PACKAGE_TYPE.SACKS:
      return defaultSacksSettings;
    case PACKAGE_TYPE.ROLLS:
      return defaultRollsSettings;
    case PACKAGE_TYPE.PIPES:
      return defaultPipesSettings;
    default:
      return defaultBoxSettings;
  }
};

export const createDefaultCargoRow = (index: number): CargoRow => ({
  id: crypto.randomUUID(),
  product: `Product ${index + 1}`,
  packageType: PACKAGE_TYPE.BOX,
  length: null,
  width: null,
  height: null,
  weight: null,
  qty: null,
  color: getRandomPaletteColor(),
  packageSettings: defaultBoxSettings,
});

const STARTER_CARGO: Array<
  Pick<CargoRow, "product" | "packageType" | "length" | "width" | "height" | "weight" | "qty" | "color">
> = [
  {
    product: "Boxes",
    packageType: PACKAGE_TYPE.BOX,
    length: 500,
    width: 400,
    height: 300,
    weight: 10,
    qty: 80,
    color: "#ffd900",
  },
  {
    product: "Big bags",
    packageType: PACKAGE_TYPE.BIG_BAGS,
    length: 1000,
    width: 1000,
    height: 1000,
    weight: 900,
    qty: 10,
    color: "#0088ff",
  },
  {
    product: "Sacks",
    packageType: PACKAGE_TYPE.SACKS,
    length: 1000,
    width: 450,
    height: 300,
    weight: 45,
    qty: 100,
    color: "#02bcb1",
  },
];

export const createStarterCargoRows = (): CargoRow[] =>
  STARTER_CARGO.map((item) => ({
    id: crypto.randomUUID(),
    ...item,
    packageSettings: createDefaultPackageSettings(item.packageType),
  }));
